// UnivaPay で会員に出す支払い手段。
//
// 決済方式（`payment.method`）が `card` = **オンライン決済（UnivaPay）** の
// ときに、その中で何を出すかを決める。コミュニティごとに違う
// （契約に銀行振込が入っていない会社もある）。
//
// 銀行振込は **UnivaPay が申込ごとに発行するバーチャル口座**のこと。
// コンビニ決済も同じく、申込ごとに支払い番号が発行される。
// どちらも入金されると通知が来るので、**自動で「支払い済み」になる**。
// 管理画面の「振込先口座」（自前の口座・手動確認）とは別物。
//
// カード以外は **その場では終わらない**（課金は `awaiting` で作られ、
// 入金されてから `successful` になる）。

export const UNIVAPAY_METHODS = ["card", "bank_transfer", "konbini"] as const;
export type UnivapayMethod = (typeof UNIVAPAY_METHODS)[number];

export const UNIVAPAY_METHOD_LABEL: Record<UnivapayMethod, string> = {
  card: "カード",
  bank_transfer: "銀行振込",
  konbini: "コンビニ決済",
};

export const UNIVAPAY_METHOD_HELP: Record<UnivapayMethod, string> = {
  card: "その場で決済が完了します。3-D セキュアは UnivaPay 側で行われます。",
  bank_transfer:
    "申込ごとに専用の振込先が発行され、入金されると自動で支払い済みになります。UnivaPay の契約に銀行振込が含まれている必要があります。",
  konbini:
    "コンビニで支払う番号が発行され、入金されると自動で支払い済みになります。UnivaPay の契約にコンビニ決済が含まれている必要があります。",
};

/** 会員が押すボタンの文言 */
export const UNIVAPAY_METHOD_ACTION: Record<UnivapayMethod, string> = {
  card: "カードで支払う",
  bank_transfer: "銀行振込で支払う",
  konbini: "コンビニで支払う",
};

/** その場では終わらない手段（画面の案内を変える） */
export function isDeferredMethod(m: UnivapayMethod): boolean {
  return m !== "card";
}

function isMethod(v: unknown): v is UnivapayMethod {
  return (UNIVAPAY_METHODS as readonly unknown[]).includes(v);
}

/**
 * 保存されている値を正す。
 *
 * **分からない値は「カードだけ」に倒す。** 契約に無い手段を出すと、
 * 会員が選んだところでエラーになるため。空も同じ扱いにする
 * （何も出ないと支払えなくなる）。
 */
export function normalizeUnivapayMethods(v: unknown): UnivapayMethod[] {
  const raw = Array.isArray(v)
    ? v
    : typeof v === "string"
      ? v.split(",")
      : [];
  const out = UNIVAPAY_METHODS.filter((m) =>
    raw.some((x) => (typeof x === "string" ? x.trim() : x) === m),
  );
  return out.length > 0 ? [...out] : ["card"];
}

/** 保存する形（順序を固定して、差分を安定させる） */
export function serializeUnivapayMethods(v: unknown): string {
  return normalizeUnivapayMethods(v).join(",");
}

export { isMethod as isUnivapayMethod };
